import { Star, Play, ImageIcon, BadgeCheck } from "lucide-react";
import { REVIEW_GALLERY, GOOGLE_REVIEWS } from "@/data/content";

function GoogleG({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.7 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.5 13.5 17.8 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.6-4.9 7.3l7.5 5.8c4.4-4 7.2-10 7.2-17.6z"/>
      <path fill="#FBBC05" d="M10.5 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.6 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.3 0 11.6-2.1 15.4-5.7l-7.5-5.8c-2.1 1.4-4.8 2.2-7.9 2.2-6.2 0-11.5-4-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
    </svg>
  );
}

function ReviewTile({ r, idx }) {
  return (
    <div
      data-testid={`gallery-review-${idx}`}
      className="review-shadow review-shadow-hover rounded-2xl border border-[var(--maqam-border)] bg-white p-6"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-serif"
            style={{ background: "var(--maqam-navy)" }}
          >
            {r.initial}
          </div>
          <div>
            <p className="text-sm font-semibold text-[color:var(--maqam-navy)] flex items-center gap-1.5">
              {r.name} <BadgeCheck size={13} className="text-[color:var(--maqam-gold)]" />
            </p>
            <p className="text-[10px] text-[var(--maqam-muted)] uppercase tracking-wider mt-0.5">{r.location}</p>
          </div>
        </div>
        <GoogleG size={16} />
      </div>
      <div className="mt-3 flex items-center gap-0.5">
        {[0, 1, 2, 3, 4].map((s) => (
          <Star key={s} size={13} className="text-[color:var(--maqam-gold)]" style={{ fill: "currentColor" }} />
        ))}
      </div>
      <p className="mt-3 text-sm text-[var(--maqam-ink)] leading-relaxed">
        {r.text.length > 200 ? r.text.slice(0, 200) + "…" : r.text}
      </p>
      <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[color:var(--maqam-gold-2)]">
        Verified Google Review · {r.date}
      </p>
    </div>
  );
}

function PhotoTile({ url, caption, idx }) {
  return (
    <figure
      data-testid={`gallery-photo-${idx}`}
      className="relative rounded-2xl overflow-hidden review-shadow"
    >
      <img src={url} alt={caption} className="w-full h-auto object-cover" loading="lazy" />
      <figcaption className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-white text-xs md:text-sm bg-gradient-to-t from-black/70 to-transparent">
        <span className="inline-flex items-center gap-1.5">
          <ImageIcon size={12} /> {caption}
        </span>
      </figcaption>
    </figure>
  );
}

function VideoTile({ idx }) {
  return (
    <div
      data-testid={`gallery-video-${idx}`}
      className="relative rounded-2xl overflow-hidden review-shadow aspect-[4/5] islamic-pattern-dark flex items-center justify-center"
    >
      <button
        aria-label="Video coming soon"
        className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[color:var(--maqam-gold)]"
      >
        <Play size={20} className="text-[color:var(--maqam-navy)] ml-1" style={{ fill: "currentColor" }} />
      </button>
      <span className="absolute bottom-4 left-4 right-4 text-white/85 text-xs uppercase tracking-[0.16em]">
        Video Testimonial · Coming Soon
      </span>
    </div>
  );
}

export default function ReviewGallery() {
  return (
    <section
      data-testid="review-gallery"
      className="py-24 md:py-32 bg-[color:var(--maqam-surface)]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-12">
          <div className="max-w-xl">
            <span className="overline">Review Gallery</span>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[color:var(--maqam-navy)] leading-[1.05] tracking-tight">
              A wall of moments, reviews & memories.
            </h2>
          </div>
          <p className="text-sm text-[var(--maqam-muted)] max-w-sm">
            A living archive of what Maqam feels like — from Google reviews to pilgrim photos to video reflections.
          </p>
        </div>

        <div className="masonry-cols">
          {REVIEW_GALLERY.map((item, i) => {
            if (item.type === "review") {
              const r = GOOGLE_REVIEWS[item.refIdx];
              if (!r) return null;
              return <ReviewTile key={i} r={r} idx={i} />;
            }
            if (item.type === "photo") {
              return <PhotoTile key={i} url={item.url} caption={item.caption} idx={i} />;
            }
            if (item.type === "video") {
              return <VideoTile key={i} idx={i} />;
            }
            return null;
          })}
        </div>
      </div>
    </section>
  );
}
